import {
  ChangeDetectionStrategy,
  Component,
  ViewChild,
  ElementRef,
  OnInit,
  OnDestroy,
  signal,
  effect,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Subscription} from 'rxjs';
import {IxModule} from '@siemens/ix-angular';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {User} from '../../../models/User';
import {UserService} from '../../../services/user.service';
import {AiQueryService} from '../../../services/ai-query.service';
import {v4 as uuidv4} from 'uuid';
import {environment} from '../../../environments/environment';

export interface ChatMessage {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
  references?: string[];
}

@Component({
  selector: 'app-ai-chatbot',
  imports: [CommonModule, FormsModule, IxModule, TranslateModule],
  templateUrl: './ai-chatbot.component.html',
  styleUrl: './ai-chatbot.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AiChatbotComponentComponent implements OnInit, OnDestroy {
  private readonly userService = inject(UserService);
  private readonly aiQueryService = inject(AiQueryService);
  private readonly translate = inject(TranslateService);

  private readonly PANE_EXPANSION_DELAY_MS = 500;

  @ViewChild('messageContainer') messageContainer!: ElementRef;

  enableAiQuery = environment.frontend.enable_ai_query === 'true';

  expanded = signal(false);
  isLoading = signal(false);
  messages = signal<ChatMessage[]>([]);
  messageText = '';
  userInitials?: string;
  userFirstName?: string;
  threadId: string | null = null;
  private activeSubscriptions: Subscription[] = [];

  async ngOnInit() {
    this.userService.userDataChange.subscribe(this.setUserData.bind(this));
  }

  ngOnDestroy() {
    this.cancelActiveRequests();
  }

  constructor() {
    // Scroll to bottom when pane is expanded and has messages
    effect(() => {
      if (this.expanded() && this.messages().length > 0) {
        setTimeout(() => this.scrollToBottom(), this.PANE_EXPANSION_DELAY_MS);
      }
    });
  }

  /**
   * Toggle the chat window open/closed
   */
  onChatButtonClick() {
    this.expanded.set(true);
  }

  /**
   * Handle pane expanded state changes
   */
  expandedChanged(event: Event) {
    setTimeout(() => {
      this.expanded.set((event as CustomEvent).detail.expanded);
    }, 0);
  }

  /**
   * Clear all messages from the chat and cancel any ongoing requests
   */
  clearChat() {
    this.cancelActiveRequests();
    this.messages.set([]);
    this.threadId = null;
    this.isLoading.set(false);
  }

  /**
   * Cancel all active HTTP requests
   */
  private cancelActiveRequests() {
    this.activeSubscriptions.forEach((sub) => sub.unsubscribe());
    this.activeSubscriptions = [];
  }

  /**
   * Send a message to the AI
   */
  sendMessage() {
    const text = this.messageText.trim();
    if (!text || this.isLoading()) {
      return;
    }

    this.sendMessageText(text);
    this.messageText = '';
  }

  /**
   * Ask an example question from the welcome screen
   */
  askExampleQuestion(questionKey: string) {
    const translatedQuestion = this.translate.instant(questionKey);
    this.sendMessageText(translatedQuestion);
  }

  /**
   * Send a message text to the AI
   */
  private sendMessageText(text: string) {
    // Add user message
    const userMessage: ChatMessage = {
      id: uuidv4(),
      content: text,
      isUser: true,
      timestamp: new Date(),
    };

    this.messages.update((msgs) => [...msgs, userMessage]);
    this.messageText = '';
    this.isLoading.set(true);

    // Create thread if not exists, then send message
    if (!this.threadId) {
      const subscription = this.aiQueryService.createThread().subscribe({
        next: (threadResponse) => {
          this.threadId = threadResponse.id;
          this.sendMessageToAI(this.threadId, text);
        },
        error: (error) => {
          this.handleAIError(error);
        },
      });
      this.activeSubscriptions.push(subscription);
    } else {
      this.sendMessageToAI(this.threadId, text);
    }

    this.scrollToBottom();
  }

  /**
   * Send message to AI service and handle response
   */
  private sendMessageToAI(threadId: string, text: string) {
    if (!threadId) {
      this.handleAIError({error: {message: 'Invalid thread ID'}});
      return;
    }

    const subscription = this.aiQueryService
      .sendMessage(threadId, text)
      .subscribe({
        next: (response) => {
          const aiMessage: ChatMessage = {
            id: uuidv4(),
            content: response.text,
            isUser: false,
            timestamp: new Date(response.createdAt),
            references: response.references,
          };
          this.messages.update((msgs) => [...msgs, aiMessage]);
          this.isLoading.set(false);
          this.scrollToBottom();
        },
        error: (error) => {
          this.handleAIError(error);
        },
      });
    this.activeSubscriptions.push(subscription);
  }

  /**
   * Handle AI service errors
   */
  private handleAIError(error?: {error?: {message?: string}}) {
    let errorContent = this.translate.instant('ai_chatbot.error.generic');

    // Handle specific error messages from backend
    if (error?.error?.message) {
      errorContent = this.translate.instant('ai_chatbot.error.specific', {
        message: error.error.message,
      });
    }

    const errorMessage: ChatMessage = {
      id: uuidv4(),
      content: errorContent,
      isUser: false,
      timestamp: new Date(),
    };
    this.messages.update((msgs) => [...msgs, errorMessage]);
    this.isLoading.set(false);
    this.scrollToBottom();
  }

  private setUserData(user: User | null) {
    if (user) {
      this.userInitials =
        `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
      this.userFirstName = user.firstName;
    }
  }

  /**
   * Handle Enter key in textarea
   */
  onEnterKey(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  /**
   * Scroll to bottom of message container
   */
  private scrollToBottom() {
    setTimeout(() => {
      if (this.messageContainer) {
        const element = this.messageContainer.nativeElement;
        element.scrollTop = element.scrollHeight;
      }
    }, 100);
  }
}
